// Rentals V1 — Web. Shared constants/types for the Request-a-Rental flow,
// reused by the card, the modal and their tests so the payload shape and the
// field limits live in exactly one place (no string literals at the call sites).
//
// Background: every rental now goes through TACH rather than the carrier
// calling a rental company directly, so this flow's only job is to get an admin
// on the phone with them. See services/emails/src/rentalRequest.ts.

import { EQUIPMENT_TYPE_OPTIONS } from './loadRequest';

/** POST endpoint behind the flow — a path, not a full URL (see AxiosWrapper). */
export const RENTAL_REQUEST_ENDPOINT = '/emails/rental-request';

/**
 * Equipment vocabulary for the dropdown. Deliberately the SAME set the load
 * board uses rather than a rentals-only copy: the backend renders both through
 * one label map (`EQUIPMENT_TYPE_LABELS`), so a second list here would be a
 * second source of truth for values the server already knows how to name.
 *
 * The field is free-form on the wire, so adding an option is a one-line change
 * here — no backend deploy. An unknown value still reaches the admin, title-cased.
 */
export const RENTAL_EQUIPMENT_OPTIONS = EQUIPMENT_TYPE_OPTIONS;

/**
 * Body of POST /api/v1/emails/rental-request.
 *
 * Every field is optional on the WIRE, matching the server's Joi schema. The
 * form is stricter: location and equipment must be answered, so the rep calling
 * back knows where to look and what to source. Location stays FREE TEXT — it
 * only has to be non-empty — so a carrier whose town never shows up in the
 * Places suggestions can still type it.
 *
 * The server stays permissive on purpose: it is the contract for any future
 * client (mobile, an admin tool), and a request that reached us with a gap
 * should still get a human on the phone rather than be rejected.
 *
 * Identity is deliberately absent; the backend derives business name, MC# and
 * contact from the auth token.
 */
export interface RentalRequestPayload {
  location?: string;
  equipment_type?: string;
  notes?: string;
}

/**
 * Caps for the two free-text fields, mirroring the server's Joi `.max()` values.
 * Enforced as `maxLength` so the carrier is stopped at the limit rather than
 * typing a paragraph and collecting a 400 on submit. Equipment needs no cap —
 * it is a <select> over our own short values.
 */
export const RENTAL_LOCATION_MAX_LENGTH = 200;
export const RENTAL_NOTES_MAX_LENGTH = 1000;
